import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ExternalLink, Loader2, Plus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { SCHEMAS, CUSTOM_TYPES } from "@/lib/sections";
import { SectionRow } from "@/components/admin/SectionRow";
import { SectionEditor } from "@/components/admin/SectionEditor";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const PAGES = [
  { id: "landing", label: "Landing agencia", url: "/" },
  { id: "portfolio", label: "Portafolio", url: "/portfolio" },
];

export default function AdminContent() {
  const { authedAxios, logout } = useAuth();
  const [page, setPage] = useState("landing");
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authedAxios().get("/admin/sections", { params: { page } });
      setSections(data);
    } catch (err) {
      toast.error("No se pudieron cargar las secciones");
      if (err?.response?.status === 401) logout();
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authedAxios, page]);

  useEffect(() => { load(); }, [load]);

  const patch = async (id, body, msg) => {
    try {
      const { data } = await authedAxios().patch(`/admin/sections/${id}`, body);
      setSections((prev) => prev.map((s) => (s.id === id ? data : s)));
      if (msg) toast.success(msg);
      return data;
    } catch {
      toast.error("No se pudo guardar");
    }
  };

  const move = async (i, d) => {
    const next = [...sections];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    setSections(next);
    try {
      await authedAxios().put("/admin/sections/reorder", { page, ids: next.map((s) => s.id) });
    } catch {
      toast.error("No se pudo reordenar");
      load();
    }
  };

  const add = async (type) => {
    try {
      const { data } = await authedAxios().post("/admin/sections", { page, type, content: { ...SCHEMAS[type].defaults } });
      setSections((prev) => [...prev, data]);
      setEditing(data);
      toast.success("Sección agregada al final");
    } catch {
      toast.error("No se pudo agregar");
    }
  };

  const confirmDelete = async () => {
    try {
      await authedAxios().delete(`/admin/sections/${deleting.id}`);
      setSections((prev) => prev.filter((s) => s.id !== deleting.id));
      toast.success("Sección eliminada");
    } catch {
      toast.error("No se pudo eliminar");
    }
    setDeleting(null);
  };

  const saveContent = async (id, content) => {
    const data = await patch(id, { content }, "Cambios guardados");
    if (data) setEditing(null);
  };

  const current = PAGES.find((p) => p.id === page);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200" data-testid="admin-content-page">
      <header className="border-b border-slate-900 bg-slate-950/80 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="admin-icon-btn" aria-label="Volver" data-testid="content-back-link"><ArrowLeft size={18} /></Link>
            <Logo size="sm" />
          </div>
          <a href={current.url} target="_blank" rel="noreferrer" className="admin-btn" data-testid="content-view-page-link">
            <ExternalLink size={14} /> Ver página
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
        <p className="font-mono text-xs text-sky-400">// contenido.editor</p>
        <h1 className="mt-2 text-3xl font-semibold text-white">Secciones del sitio</h1>
        <p className="mt-2 text-sm text-slate-400">Edita textos e imágenes, cambia el orden, oculta secciones o agrega nuevas. Los cambios se publican al guardar.</p>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
          <div className="flex gap-2" data-testid="content-page-tabs">
            {PAGES.map((p) => (
              <button key={p.id} onClick={() => setPage(p.id)} data-testid={`content-tab-${p.id}`}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors duration-200 ${page === p.id ? "bg-orange-500 text-white" : "bg-slate-900 text-slate-300 hover:text-white"}`}>
                {p.label}
              </button>
            ))}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="admin-btn-primary" data-testid="add-section-btn"><Plus size={14} /> Agregar sección</button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="border-slate-800 bg-slate-900 text-slate-200">
              {CUSTOM_TYPES.map((t) => (
                <DropdownMenuItem key={t} onClick={() => add(t)} data-testid={`add-section-${t}`} className="cursor-pointer focus:bg-slate-800 focus:text-white">
                  {SCHEMAS[t].label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-sky-400" /></div>
        ) : (
          <ul className="mt-6 space-y-2" data-testid="sections-list">
            {sections.map((s, i) => (
              <SectionRow key={s.id} section={s} index={i} total={sections.length} onMove={move}
                onToggle={(sec) => patch(sec.id, { visible: !sec.visible }, sec.visible ? "Sección oculta" : "Sección visible")}
                onEdit={setEditing} onDelete={setDeleting} />
            ))}
          </ul>
        )}
      </main>

      <SectionEditor section={editing} onClose={() => setEditing(null)} onSave={saveContent}
        onDelete={(s) => { setEditing(null); setDeleting(s); }} />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent className="border-slate-800 bg-slate-900 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar la sección "{SCHEMAS[deleting?.type]?.label}"?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">Se quitará del sitio de inmediato y no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-700 bg-slate-800 text-white" data-testid="delete-cancel-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 hover:bg-red-600" data-testid="delete-confirm-btn">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
